import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Outlet, useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from '../contexts/PermissionsContext';
import { useTenant } from '../contexts/TenantContext';
import { useLocale } from '../contexts/LocaleContext';
import { BillerLayoutContext } from '../contexts/BillerLayoutContext';
import { useTranslation } from 'react-i18next';
import { api, statsAPI, operationsAPI } from '../services/api';
import packageJson from '../../package.json';
import usePageTracking from '../hooks/usePageTracking';
import {
  CreditCardIcon,
  BellIcon,
  DocumentIcon,
  DocumentTextIcon,
  CodeBracketIcon,
  ShieldCheckIcon,
  BuildingOfficeIcon,
  CogIcon,
  UserGroupIcon,
  Bars3Icon,
  Bars3BottomLeftIcon,
  CheckIcon, // layout-2: empresa activa en el selector de la barra lateral
  XMarkIcon,
  UserIcon,
  ArrowRightOnRectangleIcon,
  CalendarDaysIcon,
  TrashIcon,
  CurrencyDollarIcon,
  DocumentDuplicateIcon,
  ShieldExclamationIcon,
  ArchiveBoxIcon,
  ChevronRightIcon,
  ChevronDownIcon,
  ChevronDoubleLeftIcon,
  ChevronDoubleRightIcon,
  WrenchScrewdriverIcon,
  BookOpenIcon,
  ChartBarIcon,
  CogIcon as CollectionConfigIcon,
  BanknotesIcon,
  RectangleStackIcon,
  ChartPieIcon,
  ServerStackIcon,
  SignalIcon,
  ClockIcon,
  CpuChipIcon,
  BeakerIcon,
  FingerPrintIcon,
  KeyIcon,
  InboxIcon,
  CloudArrowUpIcon,
  UserPlusIcon,
  PhotoIcon,
  QuestionMarkCircleIcon,
  CubeIcon, // v3.10.0 - Packages
  GlobeAmericasIcon, // v3.16.0 - Countries
  BuildingLibraryIcon, // Conciliación: Bancos
  LanguageIcon, // v3.17.0 - Translations
  MegaphoneIcon, // v3.12.0 - Medios
  ClipboardDocumentCheckIcon, // v3.22.0 - T&C
  DocumentCheckIcon, // Password Policies
  LockClosedIcon, // Password expired banner
  NewspaperIcon, // v3.23.0 - Announcements
  ArrowsRightLeftIcon // File Converters
} from '@heroicons/react/24/outline';

// Key for localStorage
const SIDEBAR_COLLAPSED_KEY = 'acmw_sidebar_collapsed';
const BILLER_RAIL_COLLAPSED_KEY = 'acmw_biller_rail_collapsed';

// Corrimiento del contenido según lo que ocupen la barra de Plataforma y la barra
// lateral de la empresa (layout-2). Las clases van escritas enteras para que
// Tailwind las encuentre; las sumas que no tienen escalón propio van con calc.
const SHELL_PADDING = {
  '0+0': '',
  '16+0': 'md:pl-16',
  '52+0': 'md:pl-52',
  '0+16': 'md:pl-16',
  '0+52': 'md:pl-52',
  '16+16': 'md:pl-32',
  '16+52': 'md:pl-[calc(theme(spacing.16)_+_theme(spacing.52))]',
  '52+16': 'md:pl-[calc(theme(spacing.16)_+_theme(spacing.52))]',
  '52+52': 'md:pl-[calc(theme(spacing.52)_*_2)]',
};

// Import UserMessages and BackgroundJobs components for modals
const UserMessages = React.lazy(() => import('./UserMessages'));
const BackgroundJobs = React.lazy(() => import('./BackgroundJobs'));
const ForceLogoutModal = React.lazy(() => import('./ForceLogoutModal'));
const UserAcceptedTcModal = React.lazy(() => import('./UserAcceptedTcModal'));
const TcAcceptanceModal = React.lazy(() => import('./TcAcceptanceModal'));
const AnnouncementModal = React.lazy(() => import('./AnnouncementModal'));

const MainLayout = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    // Initialize from localStorage
    const saved = localStorage.getItem(SIDEBAR_COLLAPSED_KEY);
    return saved === 'true';
  });
  // Barra lateral de la empresa (layout-2, doc/47 §11). BillerDashboard publica el
  // layout y dibuja su menú por portal en railElement.
  const [billerLayout, setBillerLayout] = useState(null);
  const [railElement, setRailElement] = useState(null);
  // Hueco de la fila de íconos de arriba para las acciones del dashboard (el "?"
  // del recorrido virtual, que vive en BillerDashboard).
  const [railActionsElement, setRailActionsElement] = useState(null);
  const [railOpen, setRailOpen] = useState(false);
  const [railCollapsed, setRailCollapsed] = useState(() => {
    try { return localStorage.getItem(BILLER_RAIL_COLLAPSED_KEY) === 'true'; } catch { return false; }
  });
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const [companyMenuOpen, setCompanyMenuOpen] = useState(false); // layout-2: selector de empresa de la barra lateral
  const [unreadMessagesCount, setUnreadMessagesCount] = useState(0);
  const [developersOpen, setDevelopersOpen] = useState(false);
  const [assetsOpen, setAssetsOpen] = useState(false);
  const [platformOpen, setPlatformOpen] = useState(false);
  const [adminOpen, setAdminOpen] = useState(false);
  const [iamOpen, setIamOpen] = useState(false);
  const [messagesModalOpen, setMessagesModalOpen] = useState(false);
  const [backgroundJobsModalOpen, setBackgroundJobsModalOpen] = useState(false);
  const [tcModalOpen, setTcModalOpen] = useState(false);
  const [backendVersion, setBackendVersion] = useState(null);
  const { user, logout, isAdmin, forceLogoutInfo, pendingTc, setPendingTc, pendingAnnouncements, setPendingAnnouncements, passwordExpired } = useAuth();
  const [forceLogoutData, setForceLogoutData] = useState(null);
  const [forceLogoutDismissed, setForceLogoutDismissed] = useState(false);
  const { canAccessModule, scope } = usePermissions();
  const { allowedTenants } = useTenant();
  const { icu_locale } = useLocale();
  const { t } = useTranslation(['platform', 'common']);
  const navigate = useNavigate();
  const location = useLocation();

  // Redirect to profile password tab when password is expired
  useEffect(() => {
    if (passwordExpired && location.pathname !== '/profile') {
      navigate('/profile?tab=password', { replace: true });
    }
  }, [passwordExpired, location.pathname, navigate]);

  // Track page navigation for activity log
  usePageTracking();

  // v3.19.0: Force Logout - set initial data from verify-token
  useEffect(() => {
    if (forceLogoutInfo) {
      setForceLogoutData(forceLogoutInfo);
      setForceLogoutDismissed(false);
    }
  }, [forceLogoutInfo]);

  // v3.19.0: Local countdown — force logout when remaining_seconds reaches 0
  useEffect(() => {
    if (!forceLogoutData || !forceLogoutData.remaining_seconds) return;
    const timer = setTimeout(async () => {
      await logout();
      navigate('/login');
    }, forceLogoutData.remaining_seconds * 1000);
    return () => clearTimeout(timer);
  }, [forceLogoutData?.id, forceLogoutData?.remaining_seconds]); // eslint-disable-line react-hooks/exhaustive-deps

  // v3.19.0: Force Logout polling every 30s (same cadence as messages)
  useEffect(() => {
    if (!user) return;
    const checkForceLogout = async () => {
      try {
        const res = await operationsAPI.check();
        if (res.data.success && res.data.data.active) {
          // If remaining_seconds is 0 or less, force logout immediately
          if (res.data.data.remaining_seconds <= 0) {
            await logout();
            navigate('/login');
            return;
          }
          setForceLogoutData(prev => {
            // Only reset dismissed if it's a new event or data changed
            if (!prev || prev.id !== res.data.data.id) {
              setForceLogoutDismissed(false);
            }
            return res.data.data;
          });
        } else {
          setForceLogoutData(null);
        }
      } catch {
        // silently fail
      }
    };
    // Check immediately on mount too
    checkForceLogout();
    const interval = setInterval(checkForceLogout, 30000);
    return () => clearInterval(interval);
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  // Define navigation items with their required permissions
  const allNavigationItems = [
    // Templates, Series de Datos y Notificaciones (Vectores) están dentro del menú colapsable "Activos"
    // Archivos, Publishers y Billers moved to Platform menu
  ];

  // Filter navigation based on permissions and add admin items
  const navigation = useMemo(() => {
    const items = allNavigationItems.filter(item => canAccessModule(item.module));

    // v3.5.1: Publisher users only see Dashboard in sidebar
    // All other functionality (Usuarios, Series, Developers) is accessed from within the Publisher Dashboard tabs
    if (user?.user_type === 'publisher') {
      // Get the publisherId from permissions scope (loaded from /users/{id}/scope endpoint)
      const publisherId = scope?.publisherId || scope?.publisher_id;
      if (publisherId) {
        items.push(
          { name: 'Dashboard', href: `/publisher-dashboard/${publisherId}`, icon: ChartPieIcon }
        );
      }
    }

    // v3.1.0: Biller Users management moved to BillerDashboard -> Configuración tab
    // No menu item needed here as Biller users don't see the sidebar

    // Monitoring and Channels are now inside Admin collapsible menu

    return items;
  }, [canAccessModule, isAdmin, user, scope]);

  const developersNavigation = [
    { name: t('documentation'), href: '/docs', icon: DocumentTextIcon },
    { name: t('apiDocsMenu'), href: '/apidoc', icon: CodeBracketIcon },
  ];

  // Assets navigation with permission requirements for each item
  const assetsNavigation = [
    { name: t('templateLibrary'), href: '/assets/templates', icon: BookOpenIcon, permission: 'templates' },
    { name: t('platformTemplates'), href: '/assets/platform-templates', icon: DocumentTextIcon, permission: 'platform_templates' },
    { name: t('series'), href: '/assets/series', icon: ChartBarIcon, permission: 'series_datos' },
    { name: t('logos'), href: '/assets/logos', icon: PhotoIcon, permission: 'templates' },
    { name: t('notifications'), href: '/notification-vectors', icon: BellIcon, permission: 'notification_vectors' },
    { name: t('onboarding'), href: '/assets/onboarding', icon: UserPlusIcon, permission: 'onboarding' },
    { name: t('helpTooltipsMenu'), href: '/assets/help-tooltips', icon: QuestionMarkCircleIcon, permission: 'help_tooltips' },
  ];

  // Filter assets navigation based on user permissions
  // v3.5.1: Hide Assets section for publisher users (they access Series from PublisherDashboard tabs)
  const filteredAssetsNavigation = useMemo(() => {
    // Publisher users don't see Assets in sidebar - they access it from dashboard tabs
    if (user?.user_type === 'publisher') {
      return [];
    }
    return assetsNavigation.filter(item => canAccessModule(item.permission));
  }, [canAccessModule, user]);

  const adminNavigation = [
    { name: t('tenants'), href: '/admin/tenants', icon: ServerStackIcon, permission: 'tenants' },
    { name: t('connections'), href: '/admin/connections', icon: SignalIcon, permission: 'conexiones' },
    { name: t('monitoring'), href: '/monitoring', icon: ChartPieIcon, permission: 'monitoreo' },
    { name: t('schedulers'), href: '/admin/schedulers', icon: ClockIcon, permission: 'schedulers' },
    { name: t('schedulerGroups'), href: '/admin/scheduler-groups', icon: RectangleStackIcon, permission: 'schedulers' },
    { name: t('import'), href: '/admin/import', icon: CloudArrowUpIcon },
    { name: t('aiConfig'), href: '/admin/ai-config', icon: CpuChipIcon, permission: 'ai_config' },
    { name: t('channels'), href: '/channels', icon: CogIcon, permission: 'canales' },
    { name: t('logs'), href: '/logs', icon: DocumentDuplicateIcon },
    { name: 'Operaciones', href: '/admin/operations', icon: WrenchScrewdriverIcon, permission: 'operations' },
  ];

  const iamNavigation = [
    { name: t('users'), href: '/users', icon: UserGroupIcon, permission: 'usuarios' },
    { name: t('roles'), href: '/roles', icon: ShieldCheckIcon, permission: 'roles' },
    { name: t('policies'), href: '/policies', icon: DocumentCheckIcon, permission: 'policies' },
    { name: t('oauth'), href: '/oauth-admin', icon: KeyIcon, permission: 'oauth_admin' },
  ];

  const platformNavigation = [
    { name: t('publishers'), href: '/publisher-systems', icon: ShieldCheckIcon, permission: 'publishers' },
    { name: t('billers'), href: '/billers', icon: BuildingOfficeIcon, permission: 'billers' },
    { name: t('dashboard'), href: '/platform/dashboard', icon: ChartPieIcon, permission: 'kpi' },
    { name: t('packages'), href: '/packages', icon: CubeIcon, permission: 'packages' },
    { name: t('iaExchange'), href: '/ia-exchange', icon: CpuChipIcon, permission: 'ia_exchange' },
    { name: t('convertersMenu'), href: '/platform/converters', icon: ArrowsRightLeftIcon, permission: 'file_converters' },
    { name: t('medios'), href: '/medios', icon: MegaphoneIcon, permission: 'medios' },
    { name: t('industries'), href: '/industries', icon: BuildingOfficeIcon, permission: 'industries' },
    { name: t('countries'), href: '/countries', icon: GlobeAmericasIcon, permission: 'paises' },
    { name: 'Bancos', href: '/banks', icon: BuildingLibraryIcon, permission: 'banks_catalog' },
    { name: 'Adquirentes', href: '/global-channels', icon: ArrowsRightLeftIcon, permission: 'channels_catalog' },
    { name: 'Marcas de Tarjeta', href: '/card-brands', icon: CreditCardIcon, permission: 'channels_catalog' },
    { name: 'Partners', href: '/partners', icon: UserGroupIcon, permission: 'partners' },
    { name: t('translations'), href: '/translations', icon: LanguageIcon, permission: 'traducciones' },
    { name: 'T&C', href: '/tc', icon: ClipboardDocumentCheckIcon, permission: 'terms_conditions' },
    { name: t('announcements'), href: '/announcements', icon: NewspaperIcon, permission: 'novedades' },
  ];

  // Filter admin navigation based on permissions
  // Items with permission field are shown if user has access; items without are admin-only
  const filteredAdminNavigation = useMemo(() => {
    return adminNavigation.filter(item => {
      if (item.permission) return canAccessModule(item.permission);
      return user?.role_id === 1; // admin-only items
    });
  }, [canAccessModule, user]);

  // Filter platform navigation based on permissions
  const filteredPlatformNavigation = useMemo(() => {
    return platformNavigation.filter(item => {
      if (item.permission) return canAccessModule(item.permission);
      return user?.role_id === 1; // admin-only items
    });
  }, [canAccessModule, user]);

  // Filter IAM navigation based on permissions
  const filteredIamNavigation = useMemo(() => {
    return iamNavigation.filter(item => {
      if (item.permission) return canAccessModule(item.permission);
      return user?.role_id === 1; // admin-only items
    });
  }, [canAccessModule, user]);

  // Verificar si alguna ruta de developers está activa
  const isDevelopersActive = developersNavigation.some(item => location.pathname === item.href);

  // Verificar si alguna ruta de assets está activa (solo para rutas accesibles)
  const isAssetsActive = filteredAssetsNavigation.some(item => location.pathname === item.href) ||
    (filteredAssetsNavigation.length > 0 && location.pathname.startsWith('/assets/'));

  // Verificar si alguna ruta de admin está activa
  const isAdminActive = adminNavigation.some(item => location.pathname === item.href) || location.pathname.startsWith('/admin/');

  // Verificar si alguna ruta de IAM está activa
  const isIamActive = iamNavigation.some(item => location.pathname === item.href) || location.pathname === '/oauth-admin';

  // Verificar si alguna ruta de Platform está activa
  const isPlatformActive = platformNavigation.some(item => location.pathname === item.href) || location.pathname.startsWith('/platform/') || location.pathname === '/files' || location.pathname === '/ia-exchange' || location.pathname === '/publisher-systems' || location.pathname === '/billers' || location.pathname.startsWith('/billers/') || location.pathname === '/packages' || location.pathname === '/countries' || location.pathname === '/banks' || location.pathname === '/global-channels' || location.pathname.startsWith('/global-channels/') || location.pathname === '/card-brands' || location.pathname === '/translations' || location.pathname === '/partners' || location.pathname === '/tc' || location.pathname === '/announcements';

  // Auto-expandir las secciones si estamos en una de sus rutas
  useEffect(() => {
    if (isDevelopersActive) {
      setDevelopersOpen(true);
    }
    if (isAssetsActive) {
      setAssetsOpen(true);
    }
    if (isPlatformActive) {
      setPlatformOpen(true);
    }
    if (isAdminActive) {
      setAdminOpen(true);
    }
    if (isIamActive) {
      setIamOpen(true);
    }
  }, [isDevelopersActive, isAssetsActive, isPlatformActive, isAdminActive, isIamActive]);

  // Fetch backend version info on mount
  useEffect(() => {
    statsAPI.getVersionInfo()
      .then(res => { if (res.data.success) setBackendVersion(res.data); })
      .catch(() => {});
  }, []);

  // Fetch unread messages count
  const fetchUnreadCount = useCallback(async () => {
    try {
      const response = await api.get('/messages/unread-count');
      if (response.data.success) {
        setUnreadMessagesCount(response.data.data.unread_count);
      }
    } catch (error) {
      // Silently fail - messages feature may not be critical
      console.debug('Could not fetch unread messages count:', error.message);
    }
  }, []);

  // Poll for unread messages every 30 seconds
  useEffect(() => {
    if (user) {
      fetchUnreadCount();
      const interval = setInterval(fetchUnreadCount, 30000);
      return () => clearInterval(interval);
    }
  }, [user, fetchUnreadCount]);

  // Listen for messages-updated event (emitted by UserMessages component)
  useEffect(() => {
    const handleMessagesUpdated = () => {
      fetchUnreadCount();
    };

    window.addEventListener('messages-updated', handleMessagesUpdated);
    return () => window.removeEventListener('messages-updated', handleMessagesUpdated);
  }, [fetchUnreadCount]);

  // Update unread count when modal is closed
  useEffect(() => {
    if (!messagesModalOpen) {
      fetchUnreadCount();
    }
  }, [messagesModalOpen, fetchUnreadCount]);

  // Close modals with Escape key
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        if (messagesModalOpen) {
          setMessagesModalOpen(false);
        }
        if (backgroundJobsModalOpen) {
          setBackgroundJobsModalOpen(false);
        }
        if (tcModalOpen) {
          setTcModalOpen(false);
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [messagesModalOpen, backgroundJobsModalOpen, tcModalOpen]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  // Accordion: open only one section at a time
  const openOnlySection = useCallback((section) => {
    setPlatformOpen(section === 'platform');
    setAdminOpen(section === 'admin');
    setIamOpen(section === 'iam');
    setAssetsOpen(section === 'assets');
    setDevelopersOpen(section === 'developers');
  }, []);

  // Toggle sidebar collapsed state
  const toggleSidebarCollapsed = useCallback(() => {
    if (sidebarCollapsed) {
      setSidebarCollapsed(false);
      localStorage.setItem(SIDEBAR_COLLAPSED_KEY, 'false');
      // Open first available section
      if (filteredPlatformNavigation.length > 0) openOnlySection('platform');
      else if (filteredAdminNavigation.length > 0) openOnlySection('admin');
      else if (filteredIamNavigation.length > 0) openOnlySection('iam');
      else if (filteredAssetsNavigation.length > 0) openOnlySection('assets');
      else if (user?.role_id === 1) openOnlySection('developers');
    } else {
      setSidebarCollapsed(true);
      localStorage.setItem(SIDEBAR_COLLAPSED_KEY, 'true');
    }
  }, [sidebarCollapsed, filteredPlatformNavigation, filteredAdminNavigation, filteredIamNavigation, filteredAssetsNavigation, user, openOnlySection]);

  // Handle section header click: expand sidebar if collapsed, accordion toggle
  const handleSectionClick = useCallback((sectionKey, isCurrentlyOpen) => {
    if (sidebarCollapsed) {
      setSidebarCollapsed(false);
      localStorage.setItem(SIDEBAR_COLLAPSED_KEY, 'false');
      openOnlySection(sectionKey);
    } else if (isCurrentlyOpen) {
      openOnlySection(null);
    } else {
      openOnlySection(sectionKey);
    }
  }, [sidebarCollapsed, openOnlySection]);

  // Collapse sidebar after navigating from a sub-menu item
  const handleSubMenuNav = useCallback(() => {
    setSidebarCollapsed(true);
    localStorage.setItem(SIDEBAR_COLLAPSED_KEY, 'true');
    setSidebarOpen(false);
  }, []);

  // Biller and Publisher users don't see the main sidebar - they access their own dashboards
  const hideSidebar = user?.user_type === 'biller' || user?.user_type === 'publisher';

  // ─── Navbar: empresa activa ────────────────────────────────────────────────
  // Ajuste de UX del 24/06/2026: el nombre de la empresa, el selector y el avatar
  // van todos en el navbar — nombre a la izquierda, selector y avatar a la derecha.
  // El selector vivía en una mini-fila propia dentro de BillerDashboard.
  //
  // La empresa activa se saca de la URL y no de tenantContext: en la ruta del
  // dashboard la URL es la fuente de verdad (useTenantSync la propaga al contexto
  // después), así que leer el contexto mostraría la empresa anterior mientras
  // sincroniza. Fuera de esa ruta no hay empresa activa y el bloque no se muestra.
  const activeBillerId = location.pathname.match(/^\/billers\/([^/]+)\//)?.[1] || null;

  // allowedTenants ya trae biller_description desde /auth/tenant-context, así que
  // el nombre no necesita un fetch propio.
  const activeBillerName = useMemo(() => {
    if (!activeBillerId) return null;
    const tenant = (allowedTenants || []).find(t => t.biller_id === activeBillerId);
    return (tenant?.biller_description || '').trim() || activeBillerId;
  }, [activeBillerId, allowedTenants]);

  // Mismo criterio que tenía la mini-fila: sólo un usuario de empresa con más de
  // una empresa asignada ve el selector. Reubicarlo no cambia quién lo ve.
  const showBillerSwitch = user?.user_type === 'biller'
    && (scope?.billers?.length || 0) > 1
    && !!activeBillerId;

  // La barra lateral de la empresa sólo existe dentro del dashboard de una empresa
  // con layout-2. Si hay barra de Plataforma, va a su derecha (las dos a la vez).
  const showBillerRail = billerLayout === 'layout-2' && !!activeBillerId;

  const setRailCollapsedPersist = useCallback((collapsed) => {
    setRailCollapsed(collapsed);
    try { localStorage.setItem(BILLER_RAIL_COLLAPSED_KEY, collapsed ? 'true' : 'false'); } catch { /* sin storage */ }
  }, []);

  const billerLayoutValue = useMemo(() => ({
    billerLayout,
    setBillerLayout,
    railElement,
    railActionsElement,
    railCollapsed,
  }), [billerLayout, railElement, railActionsElement, railCollapsed]);

  // Mobile: al elegir una vista (los sub-tabs son role="tab") la barra se cierra
  // para dejar ver el contenido. Elegir un módulo la deja abierta, para poder
  // elegir después la vista. Listener nativo porque los sub-tabs llegan por portal
  // desde cada módulo y sus eventos de React no suben por este árbol.
  useEffect(() => {
    if (!railElement) return undefined;
    const onClick = (e) => {
      if (e.target.closest && e.target.closest('[role="tab"]')) setRailOpen(false);
    };
    railElement.addEventListener('click', onClick);
    return () => railElement.removeEventListener('click', onClick);
  }, [railElement]);

  const platformWidth = hideSidebar ? 0 : (sidebarCollapsed ? 16 : 52);
  const railWidth = showBillerRail ? (railCollapsed ? 16 : 52) : 0;
  const shellPadding = SHELL_PADDING[`${platformWidth}+${railWidth}`] || '';
  const railLeft = hideSidebar ? '' : (sidebarCollapsed ? 'md:left-16' : 'md:left-52');

  // Iniciales del usuario para el avatar (DS: avatar con iniciales sobre blue/0
  // cuando hay nombre; el ícono genérico queda para cuando no lo hay).
  const userInitials = useMemo(() => {
    const name = (user?.full_name || '').trim();
    if (!name) return null;
    const parts = name.split(/\s+/);
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }, [user?.full_name]);

  // Avatar y opciones del usuario. Los usa el navbar y, en layout-2 (que no tiene
  // navbar), el pie de la barra lateral de la empresa.
  const userAvatar = (
    <div className="relative h-8 w-8 rounded-full bg-aura-blue-0 text-aura-neutral-0 flex items-center justify-center flex-shrink-0"
         aria-label={user?.full_name || t('user')}>
      {userInitials
        ? <span className="text-xs font-semibold" aria-hidden="true">{userInitials}</span>
        : <UserIcon className="h-5 w-5" />}
      {/* Badge de mensajes no leídos sobre el avatar - clickeable para abrir modal */}
      {unreadMessagesCount > 0 && (
        <span
          onClick={(e) => {
            e.stopPropagation();
            setMessagesModalOpen(true);
          }}
          className="absolute -top-1 -right-1 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 text-xs font-bold leading-none text-white bg-red-500 rounded-full animate-pulse cursor-pointer hover:bg-red-600 transition-colors"
          title={t('viewMessages')}
        >
          {unreadMessagesCount > 99 ? '99+' : unreadMessagesCount}
        </span>
      )}
    </div>
  );
  const profileMenuItems = (
    <div className="py-1">
      {/* User info section */}
      <div className="px-4 py-3 border-b border-gray-200">
        <p className="text-sm font-semibold text-gray-900 truncate">
          {user?.full_name || t('user')}
        </p>
        <p className="text-xs text-gray-500 truncate mt-0.5">{user?.email}</p>
        {user?.role_name && (
          <div className="mt-2">
            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800">
              {user.role_name}
            </span>
          </div>
        )}
      </div>

      {/* Menu items */}
      <button
        data-testid="btn-perfil-mensajes"
        onClick={() => {
          setProfileMenuOpen(false);
          setMessagesModalOpen(true);
        }}
        className="flex items-center justify-between w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors duration-200"
      >
        <div className="flex items-center">
          <InboxIcon className="mr-2 h-4 w-4" />
          {t('messages')}
        </div>
        {unreadMessagesCount > 0 && (
          <span className="inline-flex items-center justify-center px-2 py-0.5 text-xs font-bold leading-none text-white bg-red-500 rounded-full">
            {unreadMessagesCount > 99 ? '99+' : unreadMessagesCount}
          </span>
        )}
      </button>
      <button
        data-testid="btn-perfil-segundo-plano"
        onClick={() => {
          setProfileMenuOpen(false);
          setBackgroundJobsModalOpen(true);
        }}
        className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors duration-200"
      >
        <CloudArrowUpIcon className="mr-2 h-4 w-4" />
        {t('background')}
      </button>
      <Link
        to="/profile"
        data-testid="lnk-perfil-mi-perfil"
        onClick={() => setProfileMenuOpen(false)}
        className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors duration-200"
      >
        <UserIcon className="mr-2 h-4 w-4" />
        {t('profile')}
      </Link>
      <button
        data-testid="btn-perfil-tyc"
        onClick={() => {
          setProfileMenuOpen(false);
          setTcModalOpen(true);
        }}
        className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors duration-200"
      >
        <ClipboardDocumentCheckIcon className="mr-2 h-4 w-4" />
        T&C
      </button>
      <Link
        to="/help"
        data-testid="lnk-perfil-ayuda"
        onClick={() => setProfileMenuOpen(false)}
        className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors duration-200"
      >
        <QuestionMarkCircleIcon className="mr-2 h-4 w-4" />
        {t('title', { ns: 'help' })}
      </Link>
      <button
        data-testid="btn-logout"
        onClick={() => {
          setProfileMenuOpen(false);
          handleLogout();
        }}
        className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors duration-200"
      >
        <ArrowRightOnRectangleIcon className="mr-2 h-4 w-4" />
        {t('logout')}
      </button>

      {/* Divider */}
      <div className="border-t border-gray-200 my-1"></div>

      {/* Version info section */}
      <div className="px-4 py-2 bg-gray-50">
        <p className="text-xs font-medium text-gray-500 mb-1">{t('versions')}</p>
        <div className="space-y-0.5">
          <p className="text-xs text-gray-600">
            <span className="font-medium">Frontend:</span> v{packageJson.version}
          </p>
          <p className="text-xs text-gray-600">
            <span className="font-medium">Backend:</span>{' '}
            {backendVersion ? (
              <>v{backendVersion.version} <span className="text-gray-400">({backendVersion.gitCommit})</span></>
            ) : '...'}
          </p>
        </div>
      </div>
    </div>
  );

  return (
    <BillerLayoutContext.Provider value={billerLayoutValue}>
    <div className="min-h-screen bg-gray-50">
      {/* Mobile sidebar backdrop */}
      {!hideSidebar && sidebarOpen && (
        <div
          className="fixed inset-0 flex z-40 md:hidden"
          onClick={() => setSidebarOpen(false)}
        >
          <div className="fixed inset-0 bg-gray-600 bg-opacity-75"></div>
        </div>
      )}

      {/* Sidebar - Hidden for biller users and publisher users viewing biller dashboard */}
      {!hideSidebar && (
        <div className={`
          fixed inset-y-0 left-0 flex flex-col bg-white border-r border-gray-200 pt-5 pb-4 overflow-y-auto z-50
          transition-all duration-300 ease-in-out
          ${sidebarCollapsed ? 'w-16' : 'w-52'}
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} md:translate-x-0
        `}>
        {/* Toggle button and Logo */}
        <div className="flex flex-col flex-shrink-0">
          {/* Toggle button - hamburger style */}
          <div className={`hidden md:flex ${sidebarCollapsed ? 'justify-center px-2' : 'px-4'} mb-2`}>
            <button
              onClick={toggleSidebarCollapsed}
              title={sidebarCollapsed ? t('expandMenu') : t('collapseMenu')}
              className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-md transition-colors duration-200"
            >
              <Bars3Icon className="h-5 w-5" />
            </button>
          </div>

          {/* Logo section - only show when expanded, centered */}
          {!sidebarCollapsed && (
            <div className="flex flex-col items-center justify-center px-4 relative">
              <img
                src="/api/logos/placeholder/aura2/view"
                alt="Aura Proyecta"
                className="h-10 object-contain"
              />
              <span className="text-sm font-medium text-gray-600 mt-1">Proyecta</span>
              <button
                onClick={() => setSidebarOpen(false)}
                className="md:hidden absolute right-4 top-0 p-1 text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>
          )}
        </div>

        {/* Navigation */}
        <nav className={`mt-8 flex-1 space-y-0.5 ${sidebarCollapsed ? 'px-2' : 'px-4'}`}>
          {/* Main navigation items */}
          {navigation.map((item) => {
            const isActive = location.pathname === item.href;
            const Icon = item.icon;

            return (
              <Link
                key={item.name}
                to={item.href}
                onClick={() => setSidebarOpen(false)}
                title={sidebarCollapsed ? item.name : ''}
                className={`
                  group flex items-center py-1.5 text-sm font-medium rounded-md transition-colors duration-200
                  ${sidebarCollapsed ? 'justify-center px-2' : 'px-2'}
                  ${isActive
                    ? 'bg-blue-50 text-blue-700 border-r-2 border-blue-700'
                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                  }
                `}
              >
                <Icon
                  className={`
                    flex-shrink-0 h-5 w-5
                    ${!sidebarCollapsed ? 'mr-3' : ''}
                    ${isActive ? 'text-blue-500' : 'text-gray-400 group-hover:text-gray-500'}
                  `}
                />
                {!sidebarCollapsed && item.name}
              </Link>
            );
          })}

          {/* Platform section - show if user has access to at least one platform module */}
          {filteredPlatformNavigation.length > 0 && (
            <div className="pt-4">
              {/* Platform header */}
              <button
                onClick={() => handleSectionClick('platform', platformOpen)}
                title={sidebarCollapsed ? t('title') : ''}
                className={`
                  group flex items-center w-full py-1.5 text-sm font-medium rounded-md transition-colors duration-200
                  ${sidebarCollapsed ? 'justify-center px-2' : 'px-2'}
                  ${isPlatformActive
                    ? 'bg-green-50 text-green-700'
                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                  }
                `}
              >
                <RectangleStackIcon
                  className={`
                    flex-shrink-0 h-5 w-5
                    ${!sidebarCollapsed ? 'mr-3' : ''}
                    text-green-500
                  `}
                />
                {!sidebarCollapsed && (
                  <>
                    <span className="flex-1 text-left">{t('title')}</span>
                    {platformOpen ? (
                      <ChevronDownIcon className="ml-2 h-4 w-4" />
                    ) : (
                      <ChevronRightIcon className="ml-2 h-4 w-4" />
                    )}
                  </>
                )}
              </button>

              {/* Platform sub-menu */}
              {platformOpen && !sidebarCollapsed && (
                <div className="ml-8 mt-0.5 space-y-0.5">
                  {filteredPlatformNavigation.map((item) => {
                    const isActive = location.pathname === item.href;
                    const Icon = item.icon;

                    return (
                      <Link
                        key={item.name}
                        to={item.href}
                        onClick={handleSubMenuNav}
                        className={`
                          group flex items-center px-2 py-1.5 text-sm font-medium rounded-md transition-colors duration-200
                          ${isActive
                            ? 'bg-green-50 text-green-700 border-r-2 border-green-700'
                            : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                          }
                        `}
                      >
                        <Icon
                          className="mr-3 flex-shrink-0 h-4 w-4 text-green-500"
                        />
                        {item.name}
                      </Link>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Admin section - show if user has access to at least one admin module */}
          {filteredAdminNavigation.length > 0 && (
            <div className="pt-4">
              {/* Admin header */}
              <button
                onClick={() => handleSectionClick('admin', adminOpen)}
                title={sidebarCollapsed ? t('admin') : ''}
                className={`
                  group flex items-center w-full py-1.5 text-sm font-medium rounded-md transition-colors duration-200
                  ${sidebarCollapsed ? 'justify-center px-2' : 'px-2'}
                  ${isAdminActive
                    ? 'bg-red-50 text-red-700'
                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                  }
                `}
              >
                <ShieldExclamationIcon
                  className={`
                    flex-shrink-0 h-5 w-5
                    ${!sidebarCollapsed ? 'mr-3' : ''}
                    text-red-500
                  `}
                />
                {!sidebarCollapsed && (
                  <>
                    <span className="flex-1 text-left">{t('admin')}</span>
                    {adminOpen ? (
                      <ChevronDownIcon className="ml-2 h-4 w-4" />
                    ) : (
                      <ChevronRightIcon className="ml-2 h-4 w-4" />
                    )}
                  </>
                )}
              </button>

              {/* Admin sub-menu */}
              {adminOpen && !sidebarCollapsed && (
                <div className="ml-8 mt-0.5 space-y-0.5">
                  {filteredAdminNavigation.map((item) => {
                    const isActive = location.pathname === item.href;
                    const Icon = item.icon;

                    return (
                      <Link
                        key={item.name}
                        to={item.href}
                        onClick={handleSubMenuNav}
                        className={`
                          group flex items-center px-2 py-1.5 text-sm font-medium rounded-md transition-colors duration-200
                          ${isActive
                            ? 'bg-red-50 text-red-700 border-r-2 border-red-700'
                            : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                          }
                        `}
                      >
                        <Icon
                          className="mr-3 flex-shrink-0 h-4 w-4 text-red-500"
                        />
                        {item.name}
                      </Link>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* IAM section - show if user has access to at least one IAM module */}
          {filteredIamNavigation.length > 0 && (
            <div className="pt-4">
              {/* IAM header */}
              <button
                onClick={() => handleSectionClick('iam', iamOpen)}
                title={sidebarCollapsed ? t('iam') : ''}
                className={`
                  group flex items-center w-full py-1.5 text-sm font-medium rounded-md transition-colors duration-200
                  ${sidebarCollapsed ? 'justify-center px-2' : 'px-2'}
                  ${isIamActive
                    ? 'bg-indigo-50 text-indigo-700'
                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                  }
                `}
              >
                <FingerPrintIcon
                  className={`
                    flex-shrink-0 h-5 w-5
                    ${!sidebarCollapsed ? 'mr-3' : ''}
                    text-indigo-500
                  `}
                />
                {!sidebarCollapsed && (
                  <>
                    <span className="flex-1 text-left">{t('iam')}</span>
                    {iamOpen ? (
                      <ChevronDownIcon className="ml-2 h-4 w-4" />
                    ) : (
                      <ChevronRightIcon className="ml-2 h-4 w-4" />
                    )}
                  </>
                )}
              </button>

              {/* IAM sub-menu */}
              {iamOpen && !sidebarCollapsed && (
                <div className="ml-8 mt-0.5 space-y-0.5">
                  {filteredIamNavigation.map((item) => {
                    const isActive = location.pathname === item.href;
                    const Icon = item.icon;

                    return (
                      <Link
                        key={item.name}
                        to={item.href}
                        onClick={handleSubMenuNav}
                        className={`
                          group flex items-center px-2 py-1.5 text-sm font-medium rounded-md transition-colors duration-200
                          ${isActive
                            ? 'bg-indigo-50 text-indigo-700 border-r-2 border-indigo-700'
                            : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                          }
                        `}
                      >
                        <Icon
                          className="mr-3 flex-shrink-0 h-4 w-4 text-indigo-500"
                        />
                        {item.name}
                      </Link>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Assets section - Show if user has access to at least one asset module */}
          {filteredAssetsNavigation.length > 0 && (
            <div className="pt-4">
              {/* Assets header */}
              <button
                onClick={() => handleSectionClick('assets', assetsOpen)}
                title={sidebarCollapsed ? t('assets') : ''}
                className={`
                  group flex items-center w-full py-1.5 text-sm font-medium rounded-md transition-colors duration-200
                  ${sidebarCollapsed ? 'justify-center px-2' : 'px-2'}
                  ${isAssetsActive
                    ? 'bg-cyan-50 text-cyan-700'
                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                  }
                `}
              >
                <RectangleStackIcon
                  className={`
                    flex-shrink-0 h-5 w-5
                    ${!sidebarCollapsed ? 'mr-3' : ''}
                    text-cyan-500
                  `}
                />
                {!sidebarCollapsed && (
                  <>
                    <span className="flex-1 text-left">{t('assets')}</span>
                    {assetsOpen ? (
                      <ChevronDownIcon className="ml-2 h-4 w-4" />
                    ) : (
                      <ChevronRightIcon className="ml-2 h-4 w-4" />
                    )}
                  </>
                )}
              </button>

              {/* Assets sub-menu */}
              {assetsOpen && !sidebarCollapsed && (
                <div className="ml-8 mt-0.5 space-y-0.5">
                  {filteredAssetsNavigation.map((item) => {
                    const isActive = location.pathname === item.href;
                    const Icon = item.icon;

                    return (
                      <Link
                        key={item.name}
                        to={item.href}
                        onClick={handleSubMenuNav}
                        className={`
                          group flex items-center px-2 py-1.5 text-sm font-medium rounded-md transition-colors duration-200
                          ${isActive
                            ? 'bg-cyan-50 text-cyan-700 border-r-2 border-cyan-700'
                            : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                          }
                        `}
                      >
                        <Icon
                          className="mr-3 flex-shrink-0 h-4 w-4 text-cyan-500"
                        />
                        {item.name}
                      </Link>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Developers section - only show for admin users */}
          {user?.role_id === 1 && (
            <div className="pt-4">
              {/* Developers header */}
              <button
                onClick={() => handleSectionClick('developers', developersOpen)}
                title={sidebarCollapsed ? t('developers') : ''}
                className={`
                  group flex items-center w-full py-1.5 text-sm font-medium rounded-md transition-colors duration-200
                  ${sidebarCollapsed ? 'justify-center px-2' : 'px-2'}
                  ${isDevelopersActive
                    ? 'bg-purple-50 text-purple-700'
                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                  }
                `}
              >
                <WrenchScrewdriverIcon
                  className={`
                    flex-shrink-0 h-5 w-5
                    ${!sidebarCollapsed ? 'mr-3' : ''}
                    text-purple-500
                  `}
                />
                {!sidebarCollapsed && (
                  <>
                    <span className="flex-1 text-left">{t('developers')}</span>
                    {developersOpen ? (
                      <ChevronDownIcon className="ml-2 h-4 w-4" />
                    ) : (
                      <ChevronRightIcon className="ml-2 h-4 w-4" />
                    )}
                  </>
                )}
              </button>

              {/* Developers sub-menu */}
              {developersOpen && !sidebarCollapsed && (
                <div className="ml-8 mt-0.5 space-y-0.5">
                  {developersNavigation.map((item) => {
                    const isActive = location.pathname === item.href;
                    const Icon = item.icon;

                    return (
                      <Link
                        key={item.name}
                        to={item.href}
                        onClick={handleSubMenuNav}
                        className={`
                          group flex items-center px-2 py-1.5 text-sm font-medium rounded-md transition-colors duration-200
                          ${isActive
                            ? 'bg-purple-50 text-purple-700 border-r-2 border-purple-700'
                            : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                          }
                        `}
                      >
                        <Icon
                          className="mr-3 flex-shrink-0 h-4 w-4 text-purple-500"
                        />
                        {item.name}
                      </Link>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </nav>
        </div>
      )}

      {/* Barra lateral de la empresa — layout-2 (doc/47 §11). Mismo estilo que la
          barra de Plataforma; el menú lo dibuja BillerDashboard por portal en el
          hueco de abajo. */}
      {showBillerRail && railOpen && (
        <div
          className="fixed inset-0 flex z-40 md:hidden"
          onClick={() => setRailOpen(false)}
        >
          <div className="fixed inset-0 bg-gray-600 bg-opacity-75"></div>
        </div>
      )}
      {showBillerRail && (
        <aside
          data-testid="biller-rail"
          aria-label={t('billerDashboard.companyMenu', { ns: 'dashboard', defaultValue: 'Menú de la empresa' })}
          className={`
            fixed inset-y-0 left-0 ${railLeft} flex flex-col bg-white border-r border-gray-200 pt-5 pb-4 z-50
            transition-all duration-300 ease-in-out
            ${railCollapsed ? 'w-16' : 'w-52'}
            ${railOpen ? 'translate-x-0' : '-translate-x-full'} md:translate-x-0
          `}
        >
          {/* Arriba: el logo centrado, que además contrae y expande la barra (layout-2
              no tiene navbar, así que el logo vive acá). */}
          <div className="relative flex items-center justify-center flex-shrink-0 px-2 mb-3">
            <button
              type="button"
              onClick={() => setRailCollapsedPersist(!railCollapsed)}
              title={railCollapsed ? t('expandMenu') : t('collapseMenu')}
              aria-label={railCollapsed ? t('expandMenu') : t('collapseMenu')}
              aria-expanded={!railCollapsed}
              data-testid="btn-toggle-biller-rail"
              className="hidden md:inline-flex items-center p-1 rounded-md hover:bg-gray-100 transition-colors duration-200 focus:outline-none focus-visible:shadow-[var(--surface-focus-ring)]"
            >
              <img
                src="/api/logos/placeholder/aura2/view"
                alt="Aura Proyecta"
                className={`object-contain ${railCollapsed ? 'h-6 w-10' : 'h-10'}`}
              />
            </button>
            <img
              src="/api/logos/placeholder/aura2/view"
              alt="Aura Proyecta"
              className="md:hidden h-10 object-contain"
            />
            <button
              type="button"
              onClick={() => setRailOpen(false)}
              aria-label={t('collapseMenu')}
              data-testid="btn-close-biller-rail"
              className="md:hidden absolute right-3 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600"
            >
              <XMarkIcon className="w-6 h-6" />
            </button>
          </div>

          {/* Debajo del logo: usuario (con el badge de mensajes sin leer), selector de
              empresa (que en layout-1 van en el navbar) y el "?" del recorrido
              virtual, que BillerDashboard manda por portal al último hueco. Cada uno despliega su menú
              hacia abajo. En línea con la barra expandida, apilados si está colapsada. */}
          <div className={`flex flex-shrink-0 items-center justify-center gap-2 px-2 ${railCollapsed ? 'flex-col' : ''}`}>
            <div className="relative">
              <button
                type="button"
                onClick={() => { setCompanyMenuOpen(false); setProfileMenuOpen(!profileMenuOpen); }}
                aria-haspopup="menu"
                aria-expanded={profileMenuOpen}
                title={user?.full_name || user?.email}
                aria-label={user?.full_name || t('user')}
                data-testid="btn-rail-user-menu"
                className="flex items-center justify-center h-9 w-9 rounded-md text-gray-500 hover:text-gray-700 hover:bg-gray-100 transition-colors duration-200 focus:outline-none focus-visible:shadow-[var(--surface-focus-ring)]"
              >
                {userAvatar}
              </button>
              {profileMenuOpen && (
                <div data-testid="mnu-perfil" className="profile-dropdown absolute top-full left-0 mt-2 w-64 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 focus:outline-none z-50">
                  {profileMenuItems}
                </div>
              )}
            </div>

            {showBillerSwitch && (
              <div className="relative">
                <button
                  type="button"
                  onClick={() => { setProfileMenuOpen(false); setCompanyMenuOpen(!companyMenuOpen); }}
                  aria-haspopup="menu"
                  aria-expanded={companyMenuOpen}
                  title={t('billerDashboard.switchCompany', { ns: 'dashboard', defaultValue: 'Cambiar de empresa' })}
                  aria-label={t('billerDashboard.switchCompany', { ns: 'dashboard', defaultValue: 'Cambiar de empresa' })}
                  data-testid="btn-rail-biller-switch"
                  className="flex items-center justify-center h-9 w-9 rounded-md text-gray-500 hover:text-gray-700 hover:bg-gray-100 transition-colors duration-200 focus:outline-none focus-visible:shadow-[var(--surface-focus-ring)]"
                >
                  <BuildingOfficeIcon className="h-6 w-6" />
                </button>
                {companyMenuOpen && (
                  <div
                    role="menu"
                    data-testid="rail-biller-switch-menu"
                    className="absolute top-full left-0 mt-2 w-56 max-h-80 overflow-y-auto rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 py-1 z-50"
                  >
                    <p className="px-4 py-2 text-xs font-medium text-gray-500 border-b border-gray-200">
                      {t('billerDashboard.company', { ns: 'dashboard', defaultValue: 'Empresa:' })}
                    </p>
                    {scope.billers.map((biller) => {
                      const current = biller.biller_id === activeBillerId;
                      return (
                        <button
                          key={biller.biller_id}
                          type="button"
                          role="menuitemradio"
                          aria-checked={current}
                          data-testid={`rail-biller-option-${biller.biller_id}`}
                          onClick={() => {
                            setCompanyMenuOpen(false);
                            setRailOpen(false);
                            if (!current) navigate(`/billers/${biller.biller_id}/dashboard`);
                          }}
                          className={`flex items-center justify-between w-full px-4 py-2 text-sm text-left transition-colors duration-200 ${current ? 'bg-aura-blue-50 text-aura-blue-0 font-semibold' : 'text-gray-700 hover:bg-gray-100'}`}
                        >
                          <span className="truncate">{biller.biller_id}</span>
                          {current && <CheckIcon className="h-4 w-4 flex-shrink-0" aria-hidden="true" />}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            <div ref={setRailActionsElement} className="flex items-center justify-center" />
          </div>

          {/* Medio: el menú de la empresa (BillerDashboard, por portal). Es lo único
              que scrollea: los menús de arriba se despliegan sobre él y un
              contenedor con overflow los recortaría. */}
          <div ref={setRailElement} className={`mt-6 flex-1 min-h-0 overflow-y-auto ${railCollapsed ? 'px-2' : 'px-4'}`} />

        </aside>
      )}

      {/* Main content */}
      <div className={`flex flex-col flex-1 transition-all duration-300 ease-in-out ${shellPadding}`}>
        {/* Layout-2 no tiene navbar: en mobile, un botón flotante abre la barra lateral */}
        {showBillerRail && (
          <button
            type="button"
            onClick={() => setRailOpen(true)}
            aria-label={t('billerDashboard.companyMenu', { ns: 'dashboard', defaultValue: 'Menú de la empresa' })}
            data-testid="btn-open-biller-rail"
            className="md:hidden fixed top-3 left-3 z-30 p-2 bg-white border border-gray-200 rounded-md shadow-sm text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <Bars3BottomLeftIcon className="h-6 w-6" />
          </button>
        )}

        {/* Top navigation bar — no va en layout-2 (logo, selector y usuario van en
            la barra lateral; el nombre de la empresa no se muestra) */}
        {!showBillerRail && (
        <div className="sticky top-0 z-10 flex-shrink-0 flex h-16 bg-white shadow-sm border-b border-gray-200">
          {!hideSidebar && (
            /* Mobile menu button */
            <button
              onClick={() => setSidebarOpen(true)}
              className="px-4 border-r border-gray-200 text-gray-500 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500 md:hidden"
            >
              <Bars3Icon className="h-6 w-6" />
            </button>
          )}

          <div className="flex-1 px-4 flex justify-between items-center gap-4">
            {/* Izquierda: logo + nombre de la empresa activa.
                El logo sólo cuando el sidebar está oculto — si está visible ya lo
                muestra ahí y repetirlo sería tenerlo dos veces en pantalla. */}
            <div className="flex items-center min-w-0 gap-3">
              {hideSidebar && (
                <img
                  src="/api/logos/placeholder/aura2/view"
                  alt="Aura Proyecta"
                  className="h-8 object-contain flex-shrink-0"
                />
              )}
              {hideSidebar && activeBillerName && (
                <span className="h-6 w-px bg-aura-neutral-100 flex-shrink-0" aria-hidden="true" />
              )}
              {activeBillerName && (
                <h1
                  className="font-heading text-base font-semibold text-aura-on-surface truncate"
                  title={activeBillerName}
                  data-testid="navbar-biller-name"
                >
                  {activeBillerName}
                </h1>
              )}
            </div>

            {/* Derecha: selector de empresa + avatar, alineados horizontalmente */}
            <div className="flex items-center gap-4 flex-shrink-0">
              {showBillerSwitch && (
                <div className="flex items-center gap-2">
                  <label
                    htmlFor="navbar-biller-switch"
                    className="hidden sm:block text-sm text-aura-on-surface-variant"
                  >
                    {t('billerDashboard.company', { ns: 'dashboard', defaultValue: 'Empresa:' })}
                  </label>
                  <select
                    id="navbar-biller-switch"
                    value={activeBillerId}
                    onChange={(e) => navigate(`/billers/${e.target.value}/dashboard`)}
                    className="block px-3 py-1.5 bg-white border border-aura-outline rounded-md text-sm text-aura-on-surface focus:outline-none focus:ring-2 focus:ring-aura-blue-0 focus:border-aura-blue-0"
                    data-testid="navbar-biller-switch"
                  >
                    {scope.billers.map((biller) => (
                      <option key={biller.biller_id} value={biller.biller_id}>
                        {biller.biller_id}
                      </option>
                    ))}
                  </select>
                </div>
              )}

            {/* Profile dropdown */}
            <div className="relative">
              <button
                onClick={() => setProfileMenuOpen(!profileMenuOpen)}
                className="flex items-center space-x-2 text-sm rounded-full focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 p-2"
              >
                {userAvatar}
                <span className="hidden md:block text-sm font-medium text-aura-on-surface">
                  {user?.full_name?.split(' ')[0] || user?.email}
                </span>
              </button>

              {/* Dropdown menu */}
              {profileMenuOpen && (
                <div className="profile-dropdown origin-top-right absolute right-0 mt-2 w-64 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 focus:outline-none z-50">
                  {profileMenuItems}
                </div>
              )}
            </div>
            </div>
          </div>
        </div>
        )}

        {/* Page content - key={icu_locale} forces re-mount of all routes when locale changes */}
        {/* En mobile, layout-2 deja lugar arriba para el botón flotante del menú */}
        <main className={`flex-1 relative overflow-y-auto focus:outline-none ${showBillerRail ? 'pt-12 md:pt-0' : ''}`}>
          {passwordExpired && (
            <div className="bg-amber-500 text-white px-4 py-3 text-center text-sm font-medium shadow-sm">
              <LockClosedIcon className="h-4 w-4 inline-block mr-2 -mt-0.5" />
              {t('common:passwordExpiredBanner')}
            </div>
          )}
          <div className="py-6" key={icu_locale}>
            <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8">
              <Outlet />
            </div>
          </div>
        </main>
      </div>

      {/* Close profile menu (y el selector de empresa de layout-2) when clicking outside */}
      {(profileMenuOpen || companyMenuOpen) && (
        <div
          className="fixed inset-0 z-0"
          onClick={() => { setProfileMenuOpen(false); setCompanyMenuOpen(false); }}
        />
      )}

      {/* Messages Modal */}
      {messagesModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="messages-modal" role="dialog" aria-modal="true">
          {/* Background backdrop */}
          <div
            className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
            onClick={() => setMessagesModalOpen(false)}
          />

          {/* Modal container */}
          <div className="flex items-center justify-center min-h-screen p-4">
            {/* Modal content */}
            <div data-testid="mdl-mensajes" className="relative bg-white rounded-lg shadow-xl w-full max-w-6xl max-h-[90vh] overflow-hidden transform transition-all">
              {/* Close button */}
              <button
                data-testid="btn-mensajes-cerrar"
                onClick={() => setMessagesModalOpen(false)}
                className="absolute top-4 right-4 z-10 p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors"
                title={t('common:close')}
              >
                <XMarkIcon className="h-6 w-6" />
              </button>

              {/* Messages content */}
              <div className="p-6 overflow-y-auto max-h-[90vh]">
                <React.Suspense fallback={
                  <div className="flex items-center justify-center h-64">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                  </div>
                }>
                  <UserMessages />
                </React.Suspense>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Force Logout Modal */}
      {forceLogoutData && !forceLogoutDismissed && (
        <React.Suspense fallback={<div className="fixed inset-0 z-[60] bg-black/60" />}>
          <ForceLogoutModal
            data={forceLogoutData}
            onDismiss={() => setForceLogoutDismissed(true)}
          />
        </React.Suspense>
      )}

      {/* T&C Modal */}
      {tcModalOpen && (
        <React.Suspense fallback={
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-500 bg-opacity-75">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        }>
          <UserAcceptedTcModal onClose={() => setTcModalOpen(false)} user={user} scope={scope} />
        </React.Suspense>
      )}

      {/* Background Jobs Modal */}
      {backgroundJobsModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="background-jobs-modal" role="dialog" aria-modal="true">
          {/* Background backdrop */}
          <div
            className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
            onClick={() => setBackgroundJobsModalOpen(false)}
          />

          {/* Modal container */}
          <div className="flex items-center justify-center min-h-screen p-4">
            {/* Modal content */}
            <div data-testid="mdl-segundo-plano" className="relative bg-white rounded-lg shadow-xl w-full max-w-5xl max-h-[90vh] overflow-hidden transform transition-all">
              {/* Close button */}
              <button
                data-testid="btn-segundo-plano-cerrar"
                onClick={() => setBackgroundJobsModalOpen(false)}
                className="absolute top-4 right-4 z-10 p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors"
                title={t('common:close')}
              >
                <XMarkIcon className="h-6 w-6" />
              </button>

              {/* Background Jobs content */}
              <div className="p-6 overflow-y-auto max-h-[90vh]">
                <React.Suspense fallback={
                  <div className="flex items-center justify-center h-64">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                  </div>
                }>
                  <BackgroundJobs />
                </React.Suspense>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* T&C Acceptance Modal (blocking) */}
      {pendingTc && pendingTc.length > 0 && (
        <React.Suspense fallback={
          <div className="fixed inset-0 z-[70] bg-gray-900 bg-opacity-80 flex items-center justify-center">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-white"></div>
          </div>
        }>
          <TcAcceptanceModal
            pendingTc={pendingTc}
            user={user}
            scope={scope}
            onAccepted={() => setPendingTc(null)}
            onReject={logout}
          />
        </React.Suspense>
      )}

      {/* Announcements Modal (non-blocking, shows after T&C) */}
      {(!pendingTc || pendingTc.length === 0) && pendingAnnouncements && pendingAnnouncements.length > 0 && (
        <React.Suspense fallback={
          <div className="fixed inset-0 z-[60] bg-gray-900 bg-opacity-60 flex items-center justify-center">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-400"></div>
          </div>
        }>
          <AnnouncementModal
            announcements={pendingAnnouncements}
            onDismiss={(remaining) => setPendingAnnouncements(remaining && remaining.length > 0 ? remaining : null)}
          />
        </React.Suspense>
      )}
    </div>
    </BillerLayoutContext.Provider>
  );
};

export default MainLayout;